/*
 * SinoboomSettingsPage.jsx — super-admin console for the Sinoboom i-Link
 * integration. We bought Sinoboom MEWPs that carry Sinoboom's own telematics;
 * this page holds the i-Link API credentials (entered once, stored encrypted
 * server-side — never in git) and shows what the background sync has pulled in.
 *
 * What the sync brings today: the machine roster + full alarm history with
 * GPS. Usage telemetry (engine hours, work hours, fuel) is a Q4 i-Link
 * release, so these machines have no live status until then.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Switch,
  FormControlLabel, Alert, Chip, Divider, Grid, InputAdornment, IconButton,
  CircularProgress, Snackbar,
} from '@mui/material';
import {
  Sync as SyncIcon, Visibility, VisibilityOff, CheckCircle as OkIcon,
  ErrorOutline as ErrIcon, Save as SaveIcon,
  Troubleshoot as TroubleshootIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin } from '../../utils/permissions';
import sinoboomService from '../../services/sinoboomService';
import { formatToIST } from '../../utils/dataUtils';

const Stat = ({ label, value, color = 'text.primary' }) => (
  <Box>
    <Typography variant="h5" fontWeight={800} sx={{ color }}>{value ?? '—'}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
  </Box>
);

const SinoboomSettingsPage = () => {
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [stored, setStored] = useState({});
  const [form, setForm] = useState({ username: '', password: '', host: 'ailink.sinoboom.com', enabled: false, poll_minutes: 10 });
  const [showPass, setShowPass] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    try {
      const r = await sinoboomService.getSettings();
      setSettings(r.settings);
      setStored(r.stored || {});
      setForm(f => ({
        ...f,
        username: r.settings.username || '',
        host: r.settings.host || 'ailink.sinoboom.com',
        enabled: !!r.settings.enabled,
        poll_minutes: r.settings.poll_minutes || 10,
        password: '',   // never prefilled
      }));
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.message || e.message || 'Failed to load settings', severity: 'error' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (superAdmin) load(); else setLoading(false); }, [superAdmin, load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = { username: form.username, host: form.host, enabled: form.enabled, poll_minutes: form.poll_minutes };
      if (form.password) patch.password = form.password;   // only send when changed
      const r = await sinoboomService.saveSettings(patch);
      setSettings(r.settings);
      setForm(f => ({ ...f, password: '' }));
      setSnack({ open: true, message: 'Settings saved.', severity: 'success' });
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.message || e.message || 'Save failed', severity: 'error' });
    } finally { setSaving(false); }
  };

  // Test the credentials WITHOUT saving them — the whole point when you have
  // just been handed a login and want to know if it works before committing it.
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const body = {};
      if (form.username) body.username = form.username;
      if (form.password) body.password = form.password;   // unsaved value, if typed
      if (form.host) body.host = form.host;
      setTestResult(await sinoboomService.testConnection(body));
    } catch (e) {
      setTestResult({ success: false, steps: [{ step: 'request', ok: false, detail: e.response?.data?.message || e.message || 'Test failed' }] });
    } finally { setTesting(false); }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const r = await sinoboomService.syncNow();
      const res = r.result;
      setSettings(r.settings);
      await load();
      setSnack({
        open: true,
        message: res ? `Sync done — ${res.machines} machines, ${res.imported} new alarms (tenant total ${res.total}).` : 'Sync ran.',
        severity: 'success',
      });
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.message || e.message || 'Sync failed', severity: 'error' });
    } finally { setSyncing(false); }
  };

  if (!superAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">The Sinoboom integration settings are available to the super admin only.</Alert>
      </Box>
    );
  }
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const fmt = (ts) => {
    if (!ts) return '—';
    const s = formatToIST(ts, 'dd MMM, HH:mm');
    return s === 'N/A' || s === 'Invalid Date' ? '—' : s;
  };
  const statusChip = () => {
    const st = settings?.last_status;
    if (!st) return <Chip size="small" label="Never run" variant="outlined" />;
    if (st === 'OK') return <Chip size="small" color="success" icon={<OkIcon />} label="OK" />;
    if (st === 'RUNNING') return <Chip size="small" color="info" icon={<CircularProgress size={12} />} label="Running" />;
    return <Chip size="small" color="error" icon={<ErrIcon />} label={st} />;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 980 }} className="animate-fade-in">
      {/* Status strip */}
      <Card sx={{ borderRadius: '16px', mb: 2.5 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={800}>Sync status</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {statusChip()}
              <Button variant="outlined" startIcon={testing ? <CircularProgress size={16} /> : <TroubleshootIcon />}
                onClick={handleTest} disabled={testing} sx={{ fontWeight: 700 }}>
                {testing ? 'Testing…' : 'Test connection'}
              </Button>
              <Button variant="contained" startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                onClick={handleSyncNow} disabled={syncing || !settings?.configured} sx={{ fontWeight: 700 }}>
                {syncing ? 'Syncing…' : 'Sync now'}
              </Button>
            </Stack>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><Stat label="Machines synced" value={stored.machines} color="primary.main" /></Grid>
            <Grid item xs={6} sm={3}><Stat label="Alarms stored" value={stored.alarms} /></Grid>
            <Grid item xs={6} sm={3}><Stat label="Active now" value={stored.active} color={stored.active ? 'error.main' : 'text.primary'} /></Grid>
            <Grid item xs={6} sm={3}><Stat label="Last run" value={fmt(settings?.last_run_at)} /></Grid>
          </Grid>
          {settings?.last_error && (
            <Alert severity="error" sx={{ mt: 2 }}>{settings.last_error}</Alert>
          )}

          {/* Connection test — one line per step, so a failure names ITSELF
              instead of leaving "the sync isn't working" to be guessed at. */}
          {testResult && (
            <Box sx={{ mt: 2, p: 2, borderRadius: '12px', border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                Connection test — {testResult.success ? 'all steps passed' : 'stopped at a failing step'}
                {testResult.host ? ` · ${testResult.host}` : ''}
              </Typography>
              {(testResult.steps || []).map((st, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.5 }}>
                  {st.ok
                    ? <OkIcon sx={{ fontSize: 18, color: 'success.main', mt: '2px' }} />
                    : <ErrIcon sx={{ fontSize: 18, color: 'error.main', mt: '2px' }} />}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {st.step} — {st.ok ? 'OK' : 'failed'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                      {st.detail}
                    </Typography>
                    {st.hint && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', fontWeight: 600 }}>
                        {st.hint}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {!settings?.configured && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Enter the i-Link username and password below, turn the sync on, and save. The first sync
              runs within a couple of minutes (or press <b>Sync now</b>).
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Credentials */}
      <Card sx={{ borderRadius: '16px' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>i-Link credentials</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
            Stored encrypted on the server, never in the browser or in git. Use a dedicated i-Link
            account — logging into the i-Link website with the same account signs the sync out.
            The password is the <b>MD5-hashed</b> value i-Link expects (as Sinoboom provided).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="i-Link username"
                value={form.username} onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))}
                placeholder="purchase.mrl1@mtandt.com" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={settings?.has_password ? 'Password (leave blank to keep)' : 'Password (MD5)'}
                type={showPass ? 'text' : 'password'}
                value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPass(s => !s)} edge="end">
                        {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="API host"
                value={form.host} onChange={(e) => setForm(f => ({ ...f, host: e.target.value }))}
                helperText="ailink.sinoboom.com (global) — no change needed normally" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="Poll interval (minutes)"
                value={form.poll_minutes} onChange={(e) => setForm(f => ({ ...f, poll_minutes: e.target.value }))}
                inputProps={{ min: 5 }} helperText="How often to pull new alarms (min 5)" />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2.5 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
            <FormControlLabel
              control={<Switch checked={form.enabled} onChange={(e) => setForm(f => ({ ...f, enabled: e.target.checked }))} />}
              label={<Typography fontWeight={700}>{form.enabled ? 'Sync enabled' : 'Sync paused'}</Typography>} />
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave} disabled={saving} sx={{ fontWeight: 700 }}>
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ fontWeight: 600 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SinoboomSettingsPage;
